import { useEffect } from 'react';
import { isApolloError, useApolloClient } from '@apollo/client';
import { initializeApollo } from '@config/apollo';
import { Product, Category } from '@interfaces';
import {
  getProduct,
  getProductQueryParams,
  getProductQueryResponse,
} from '@queries/product/queries';

import { Console, getRoute } from '@utils';
import { GetServerSideProps } from 'next';

import { useRouter } from 'next/router';

import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { validate as validateUuid } from 'uuid';
import {
  getProductSlug,
  getProductSlugQueryParams,
  getProductSlugQueryResponse,
} from '@queries/product/queries/getProductSlug';
import { BASE_URL, NEXT_PUBLIC_NO_PRICE_FALLBACK } from '@config/environment';
import { SEOHead } from '@components/seo';
import { ProductPage } from '@screens/product';

interface ProductProps {
  initialProduct: Product;
  initialCategories: Category[];
  statusCode: number;
}

export default function ShowProduct({ initialProduct: product }: ProductProps) {
  const router = useRouter();
  const apolloClient = useApolloClient();
  useEffect(
    function redirectToBaseWithModal() {
      if (!product?.slug) return;
      apolloClient.writeQuery({
        query: getProduct('getProductInShowDeailtStaticProps'),
        variables: {
          params: { slug: product.slug },
        },
        data: { product },
      });
    },
    [router, product?.slug, product, apolloClient],
  );
  const currFormat = new CurrencyFormatter({
    fallback: NEXT_PUBLIC_NO_PRICE_FALLBACK,
  });
  Console.log({ msg: '[FLICKER] prodSlug', product });
  useEffect(
    function redirectNoProductFound() {
      if (!product) {
        void router.push({ pathname: '/404' });
      }
    },
    [product, router],
  );
  return (
    <>
      <SEOHead
        title={
          product
            ? `${product.name}${
                product.concentration ? ' ' + product.concentration : ''
              }${
                product?.discount?.price
                  ? ` • ${currFormat.format(product?.discount?.price)}`
                  : product.price
                  ? ` • ${currFormat.format(product.price)}`
                  : ''
              }`
            : 'Meki • Productos'
        }
        canonical={`${BASE_URL}/producto/${product?.slug}`}
        description={
          product
            ? product.quantity.toString() +
              ' ' +
              product.format +
              (product.activePrinciple
                ? ` • ${product.activePrinciple}`
                : ' ') +
              (product.laboratory ? ` • ${product.laboratory}` : '')
            : ''
        }
        customImage={{
          url: product?.thumbnailUrl,
          type: 'image/png',
          width: '256',
          height: '256',
        }}
      />
      {product && <ProductPage />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (data) => {
  const {
    params: { prodSlug },
    res,
  } = data;

  const apolloClient = initializeApollo();
  if (validateUuid(prodSlug as string)) {
    const {
      data: { product },
    } = await apolloClient.query<
      getProductSlugQueryResponse,
      getProductSlugQueryParams
    >({
      query: getProductSlug(),
      variables: {
        params: { uuid: typeof prodSlug === 'string' ? prodSlug : prodSlug[0] },
      },
    });
    res.setHeader('Cache-Control', `public, max-age=0, s-maxage=86400`);
    return {
      redirect: {
        destination: '/producto/' + product.slug,
        permanent: true,
      },
    };
  }
  try {
    const {
      data: { product },
    } = await apolloClient.query<
      getProductQueryResponse,
      getProductQueryParams
    >({
      query: getProduct('getProductInShowDeailtStaticProps'),
      variables: {
        params: { slug: typeof prodSlug === 'string' ? prodSlug : prodSlug[0] },
      },
    });
    if (product) {
      res.setHeader('Cache-Control', `public, max-age=0, s-maxage=86400`);
      return {
        props: {
          initialProduct: product,
          statusCode: 200,
        },
      };
    }
    return {
      props: {
        statusCode: 404,
      },
    };
  } catch (e) {
    Console.error(
      '[PRODUCT] Error prefetching product ' +
        (typeof prodSlug === 'string' ? prodSlug : prodSlug.join(' · ')),
    );
    Console.error(e);
    if (isApolloError(e)) {
      for (const error of e.graphQLErrors) {
        Console.log({ graphQLError: error });
      }
      Console.log({ networkError: e.networkError });
    }
    return {
      props: {},
    };
  }
};
